import {
  ApolloClient,
  DefaultOptions,
  split,
  from,
  ApolloLink,
  Operation,
  HttpLink
} from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { WebSocketLink } from "@apollo/client/link/ws";
import { onError } from "@apollo/client/link/error";
import { getMainDefinition } from "apollo-utilities";
import { createUploadLink } from "apollo-upload-client";
import { SubscriptionClient } from "subscriptions-transport-ws";
import { omitDeep } from "@bonliva-crm/common";
import { authLink } from "./authLink";
import { onLogout } from "./onLogout";
import { cache } from "./cache";
import { CURRENT_TENANT_KEY, getCurrentTenant } from "../tenant/tenant";
import { getToken } from "../msalConfig";
import { dbNameLink } from "../context/db-name-context";
import { Tenant } from "@bonliva-crm/shared";
import { environment } from "../../environments/environment";

const isFile = (value: any) =>
  (typeof File !== "undefined" && value instanceof File) ||
  (typeof FileList !== "undefined" && value instanceof FileList) ||
  (typeof Blob !== "undefined" && value instanceof Blob);

const isUpload = (operation: Operation) =>
  Object.values(operation.variables).some(isFile);

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const code = err.extensions?.code;

      if (code === "INVALID_TENANT") {
        const redirect = err.extensions?.redirectTo || Tenant.BonlivaAB;
        console.warn(`[GraphQL] Invalid tenant. Redirecting to: ${redirect}`);
        localStorage.setItem(CURRENT_TENANT_KEY, redirect);
        window.dispatchEvent(new Event("tenantChanged"));
      }
    }
  }

  if (networkError) {
    console.error("[Network error]", networkError);
  }
});

const createOmitTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = omitDeep(operation.variables, "__typename");
  }
  return forward(operation);
});

export function createClient(
  uri: string,
  subscriptionsUrl?: string,
  defaultOptions: DefaultOptions = {}
) {
  const tenant = getCurrentTenant();

  const httpLink = new HttpLink({
    credentials: "include",
    uri,
    headers: {
      "X-CRM-TENANT": tenant,
      "X-CRM-VERSION": environment.version,
      "X-CRM-BUILD": environment.build
    }
  });

  const uploadLink = createUploadLink({
    credentials: "include",
    uri,
    headers: {
      "X-CRM-TENANT": tenant,
      "X-CRM-VERSION": environment.version,
      "X-CRM-BUILD": environment.build
    }
  });

  const wsClient = subscriptionsUrl
    ? new SubscriptionClient(subscriptionsUrl, {
        lazy: true,
        reconnect: true,
        timeout: 30000,
        connectionParams: async () => {
          const token = await getToken();

          return { token };
        }
      })
    : null;

  const wsLink = subscriptionsUrl ? new WebSocketLink(wsClient) : null;

  if (wsClient) {
    wsClient.onReconnected(() => {
      console.log("WebSocket reconnected");
    });
  }

  const shouldRetry = (error: any) => {
    // Check if the error code is 502 or 504
    return error.statusCode === 502 || error.statusCode === 504; // Do not retry for other error codes
  };

  const retryLink = new RetryLink({
    delay: { initial: 300, max: 10000, jitter: true },
    attempts: { max: 3, retryIf: (e) => shouldRetry(e) }
  });

  const link = wsLink
    ? split(
        ({ query }) => {
          const def = getMainDefinition(query as any);
          return def.kind === "OperationDefinition" && def.operation === "subscription";
        },
        wsLink as any,
        httpLink
      )
    : httpLink;

  const links = from([
    onLogout,
    authLink,
    errorLink,
    dbNameLink,
    retryLink as any,
    createOmitTypenameLink,
    link
  ]);

  const terminatingLink = split(isUpload, from([authLink, uploadLink]), links);

  return new ApolloClient({
    link: terminatingLink,
    cache,
    connectToDevTools: true,
    defaultOptions
  });
}
